import PopoverButton from 'components/Shared/Button/PopoverButton'
import {
    CampaignManagement,
    CampaignStatusControl,
    TemplatedCampaignManagement,
} from 'components/Shared/Permission/enums'
import { useCheckSomePermissions, usePermission } from 'hooks/permissions'
import MenuItem from 'particles/MenuItem'
import Modal from 'particles/Modal'
import styled from 'particles/themes/styled'
import React, { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { showSnackBar } from 'services/toast'
import { selectCampaignByTemplateData } from 'state'
import {
    exportTemplate,
    publishCampaignTemplate,
    selectTemplateData,
    sendToEdit,
    requestPayment,
    sendToReview,
    selectCampaignSchedule,
} from 'state/campaignTemplates'
import { CAMPAIGN_STATUS_ENUM, clearCampaignList } from 'state/campaigns'
import Progress from './TemplateSelector/Progress'
import RectangleButton from 'particles/Button/RectangleButton'
import { useTranslation } from 'react-i18next'
import { dateIsInPast, getStartOfToday } from 'services/dates'
import { saveTemplateFormError } from 'state/error/campaignTemplates'
import { ERROR_CODES } from 'state/error'

interface TemplateActionsWrapperProps {
    templateUuid: string | null
}

interface TemplateActionsForLiveProps extends TemplateActionsWrapperProps {
    setShowExportModal: React.Dispatch<React.SetStateAction<boolean>>
}

interface TemplateActionsProps extends TemplateActionsWrapperProps {
    setShowExportModal: React.Dispatch<React.SetStateAction<boolean>>
    setShowToReviewModal: React.Dispatch<React.SetStateAction<boolean>>
}

export const TemplateActions: React.FC<TemplateActionsProps> = ({
    templateUuid,
    setShowExportModal,
    setShowToReviewModal,
}) => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()

    const campaign = useSelector(selectCampaignByTemplateData)
    const campaignSchedule = useSelector(selectCampaignSchedule)
    const ableToLaunch = usePermission(CampaignStatusControl.LaunchCampaign)
    const ableToReview = usePermission(CampaignStatusControl.ReviewCampaign)
    const isInternalPermission = usePermission(TemplatedCampaignManagement.CreateAndEditBriefsForInternal)

    // actions are only for internal permission & mentioned status
    const displayByStatusAndPermission =
        (campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        isInternalPermission
    const ableToExport =
        usePermission(CampaignManagement.CreateAndEditBriefsForInternal) &&
        campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW

    const noActionsAllowed = !ableToReview && !ableToLaunch && !ableToExport
    const ableToRequestPayment =
        ableToLaunch && campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW && campaign.has_budget
    const ableToSendForReview = ableToLaunch && campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT
    const ableToSendForEdit = ableToReview && campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW

    if (noActionsAllowed || !displayByStatusAndPermission || !templateUuid) return null

    const customStartDate = getStartOfToday(campaign.country)

    const popoverActions = [
        {
            itemPrimaryLabel: t('button.launchCampaign'),
            onClick() {
                if (
                    !!campaignSchedule &&
                    dateIsInPast(campaignSchedule.campaign_time_window.start, campaign.country, customStartDate)
                ) {
                    dispatch(
                        saveTemplateFormError({
                            campaign_schedule: {
                                start_time: [
                                    {
                                        code: ERROR_CODES.SCHEDULE_MUST_LATER_THAN_NOW,
                                    },
                                ],
                            },
                        }),
                    )
                    showSnackBar({
                        title: t('errorMessage.validation.enterDetailsRedSubtext'),
                        type: 'error',
                    })
                } else {
                    dispatch(
                        publishCampaignTemplate({
                            templateUuid,
                            onSuccessCallback: () => {
                                dispatch(clearCampaignList())
                                history.push('/campaigns')
                                showSnackBar({
                                    type: 'positive',
                                    title: t('successMessage.launchCampaign'),
                                })
                            },
                        }),
                    )
                }
            },
            show: ableToLaunch,
            disabled: false,
        },
        {
            itemPrimaryLabel: t('button.sendPaymentRequest'),
            onClick() {
                dispatch(
                    requestPayment({
                        templateUuid,
                        onSuccessCallback: () => {
                            dispatch(clearCampaignList())
                            history.push('/campaigns')
                            showSnackBar({
                                type: 'positive',
                                title: t('successMessage.campaignCreation.sendPaymentRequest'),
                            })
                        },
                    }),
                )
            },
            show: ableToRequestPayment,
            disabled: false,
        },
        {
            itemPrimaryLabel: t('button.sendBackToReview'),
            onClick() {
                setShowToReviewModal(true)
            },
            show: ableToSendForReview,
            disabled: false,
        },
        {
            itemPrimaryLabel: t('button.sendToEdit'),
            onClick() {
                dispatch(
                    sendToEdit({
                        templateUuid,
                        onSuccessCallback: () => {
                            dispatch(clearCampaignList())
                            history.push('/campaigns')
                            showSnackBar({
                                type: 'positive',
                                title: t('successMessage.campaignMovedToForEdit'),
                            })
                        },
                    }),
                )
            },
            show: ableToSendForEdit,
        },
    ]

    // show divider when at least one popoverActions item show = true
    const showDivider = !!popoverActions.filter(action => action.show).length

    return (
        <TemplateActionContainer>
            <PopoverButton
                popoverTitleText=""
                buttonProps={{
                    label: t('button.selectAction'),
                    icon: 'arrowUpRight',
                }}
            >
                {(closePopup: () => void) => (
                    <MenuGroup>
                        {popoverActions.map(
                            (option, index) =>
                                option.show && (
                                    <MenuItem.Text
                                        key={`popoverButton-${index}`}
                                        text={option.itemPrimaryLabel}
                                        onClick={e => {
                                            option.onClick()
                                            closePopup()
                                        }}
                                        isDisabled={option.disabled}
                                        tabIndex={index}
                                    />
                                ),
                        )}

                        {ableToExport && (
                            <>
                                {showDivider && <Divider />}

                                <MenuItem.Text
                                    key={`popoverButton-${popoverActions.length + 1}`}
                                    text={t('button.transferToCampaignEditor')}
                                    onClick={e => {
                                        setShowExportModal(true)
                                        closePopup()
                                    }}
                                    tabIndex={popoverActions.length}
                                />
                            </>
                        )}
                    </MenuGroup>
                )}
            </PopoverButton>
        </TemplateActionContainer>
    )
}

export const TemplateActionsForLive: React.FC<TemplateActionsForLiveProps> = ({ templateUuid, setShowExportModal }) => {
    const [t] = useTranslation()
    // This is to control when to hide the whole container
    const saveButtonPortalRef = useRef<HTMLDivElement>(null)

    const canExportToEditor = useCheckSomePermissions([
        CampaignManagement.LiveCampaignEditBriefs,
        CampaignManagement.LiveCampaignEditDeadlines,
    ])
    const canSaveChangesPermission = useCheckSomePermissions([
        TemplatedCampaignManagement.LiveCampaignEditBriefsAndDeadlines,
    ])
    const saveButtonPortalCount = saveButtonPortalRef.current ? saveButtonPortalRef.current.childNodes.length : 0
    const canSaveChanges = saveButtonPortalCount > 0 && canSaveChangesPermission
    if (!templateUuid) return null

    return (
        <TemplateActionContainer className={`${!canSaveChanges && !canExportToEditor && 'hide'}`}>
            {canExportToEditor && (
                <RectangleButton
                    form="outlined"
                    size="m"
                    label={t('button.transferToCampaignEditor')}
                    type="button"
                    onClick={e => {
                        e.preventDefault()
                        setShowExportModal(true)
                    }}
                    data-testid="test-transfer-to-editor-button"
                />
            )}
            <div
                id="template-save-button-portal"
                ref={saveButtonPortalRef}
                className={`${!canSaveChangesPermission && 'hide'}`}
            />
        </TemplateActionContainer>
    )
}

const TemplateActionsWrapper: React.FC<TemplateActionsWrapperProps> = props => {
    const campaign = useSelector(selectCampaignByTemplateData)
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const templateData = useSelector(selectTemplateData)
    const [showExportModal, setShowExportModal] = useState(false)
    const [showToReviewModal, setShowToReviewModal] = useState(false)
    const [showProgressModal, setShowProgressModal] = useState(false)
    const [campaignSetupProgress, setCampaignSetupProgress] = useState(0)

    const updateProgress = (cb: () => void) => {
        const totalDuration = 3000 // 3 seconds
        const updateInterval = 100 // Update every 100 milliseconds
        let progress = 0
        const step = (updateInterval / totalDuration) * 100 // Calculate the step to increment

        const intervalId = setInterval(() => {
            progress += step
            if (progress >= 100) {
                setCampaignSetupProgress(100)
                clearInterval(intervalId)
                cb()
            } else {
                setCampaignSetupProgress(progress)
            }
        }, updateInterval)
    }

    const handleExport = () => {
        setShowProgressModal(true)
        setShowExportModal(false)
        props.templateUuid &&
            campaign &&
            dispatch(
                exportTemplate({
                    templateUuid: props.templateUuid,
                    campaignUuid: campaign?.uuid,
                    onSuccessCallback: () => {
                        updateProgress(() => {
                            history.replace(`/campaign/edit/registration?campaign=${templateData?.campaign.uuid}`)
                            showSnackBar({
                                title: t('campaign.template.createCampainSuccessfully.title'),
                                type: 'positive',
                            })
                        })
                    },
                    onErrorCallback: () => {
                        setShowProgressModal(false)
                    },
                }),
            )
    }

    const handleSendToReview = () => {
        setShowToReviewModal(false)
        props.templateUuid &&
            dispatch(
                sendToReview({
                    templateUuid: props.templateUuid,
                    onSuccessCallback: () => {
                        dispatch(clearCampaignList())
                        history.push('/campaigns')
                        showSnackBar({
                            type: 'positive',
                            title: t('successMessage.campaignCreation.movedBackToReview'),
                        })
                    },
                }),
            )
    }

    return (
        <>
            {campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE ? (
                <TemplateActionsForLive {...props} setShowExportModal={setShowExportModal} />
            ) : (
                <TemplateActions
                    {...props}
                    setShowExportModal={setShowExportModal}
                    setShowToReviewModal={setShowToReviewModal}
                />
            )}
            {showProgressModal && (
                <Modal
                    title={t('campaign.template.transferToCampaignEdiorProgress.title')}
                    subtext={t('campaign.template.transferToCampaignEdiorProgress.subtext')}
                    size={'l'}
                    hasButtonSet={false}
                    hasIcon={false}
                    isClosable={false}
                >
                    <Progress count={campaignSetupProgress} total={100} />
                </Modal>
            )}
            {showExportModal && (
                <Modal
                    title={t('transferToCampaignEditor.Modal.title')}
                    subtext={t('transferToCampaignEditor.Modal.subtext')}
                    size={'s'}
                    isClosable
                    hasButtonSet
                    onConfirm={handleExport}
                    onClose={() => setShowExportModal(false)}
                    primaryButtonProps={{
                        label: t('button.transfer'),
                    }}
                    secondaryButtonProps={{
                        label: t('helper.uploadProgress.inProgress.button.cta1'),
                    }}
                />
            )}
            {showToReviewModal && (
                <Modal
                    title={t('modalWindow.sendBackToReviewTitle')}
                    subtext={t('modalWindow.sendBackToReviwSubtext')}
                    size={'s'}
                    isClosable
                    hasButtonSet
                    hasIcon={false}
                    onConfirm={handleSendToReview}
                    onClose={() => setShowToReviewModal(false)}
                    primaryButtonProps={{
                        label: t('button.sendBackToReview'),
                    }}
                    secondaryButtonProps={{
                        label: t('helper.uploadProgress.inProgress.button.cta1'),
                    }}
                />
            )}
        </>
    )
}

export default TemplateActionsWrapper

const TemplateActionContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    height: 60px;
    padding: 2px 24px 2px 0px;
    box-sizing: border-box;
    background: ${({ theme }) => theme.colors.surface.primary};
    box-shadow: 0px 1px 8px 0px rgba(0, 0, 0, 0.06);
    gap: ${({ theme }) => theme.spacing[600]};
    &.hide {
        display: none;
    }
`

const MenuGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
`

const Divider = styled.div`
    width: 100%;
    border-bottom: 1px solid ${({ theme }) => theme.colors.divider.primary};
`
